/**
 * useDeviceMotion — accelerometer-based motion magnitude for adaptive FPS.
 *
 * Listens to DeviceMotionEvent and computes a normalized motion magnitude (0–1)
 * from the accelerometer's acceleration (excluding gravity) using a rolling
 * window average (~500ms).
 *
 * On iOS 13+, requestPermission() must be called from a user gesture before
 * events will fire.
 *
 * Falls back gracefully: if DeviceMotionEvent is not supported (desktop browsers),
 * isAvailable = false and motionMagnitude defaults to 0.5 (mid-range).
 */

import { useRef, useState, useCallback, useEffect } from 'react'

/** Maximum raw acceleration magnitude we consider "fast motion" (m/s²). */
const MAX_ACCELERATION = 8

/** Rolling window size in samples. At ~60Hz events, 30 samples ≈ 500ms. */
const WINDOW_SIZE = 30

interface UseDeviceMotionReturn {
  /** Normalized 0–1. 0 = still, 1 = fast motion. Defaults to 0.5 when unavailable. */
  motionMagnitude: number
  /** Whether the DeviceMotion API is available and permitted. */
  isAvailable: boolean
  /** Call from a user gesture (tap/click) to request iOS permission. Returns true if granted. */
  requestPermission: () => Promise<boolean>
}

export function useDeviceMotion(): UseDeviceMotionReturn {
  const [motionMagnitude, setMotionMagnitude] = useState(0.5)
  const [isAvailable, setIsAvailable] = useState(false)
  const windowRef = useRef<number[]>([])
  const listeningRef = useRef(false)

  const handleMotion = useCallback((event: DeviceMotionEvent) => {
    const acc = event.acceleration
    if (!acc || acc.x == null || acc.y == null || acc.z == null) return

    const magnitude = Math.sqrt(acc.x ** 2 + acc.y ** 2 + acc.z ** 2)

    // Push to rolling window
    const win = windowRef.current
    win.push(magnitude)
    if (win.length > WINDOW_SIZE) win.shift()

    // Average the window
    const avg = win.reduce((sum, v) => sum + v, 0) / win.length

    // Normalize to 0–1
    const normalized = Math.min(avg / MAX_ACCELERATION, 1)
    setMotionMagnitude(normalized)
  }, [])

  const startListening = useCallback(() => {
    if (listeningRef.current) return
    listeningRef.current = true
    window.addEventListener('devicemotion', handleMotion, { passive: true })
    setIsAvailable(true)
  }, [handleMotion])

  const requestPermission = useCallback(async (): Promise<boolean> => {
    // iOS 13+ requires explicit permission request from a user gesture
    const DME = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied' | 'default'>
    }

    if (typeof DME.requestPermission === 'function') {
      try {
        const result = await DME.requestPermission()
        if (result === 'granted') {
          startListening()
          return true
        }
        return false
      } catch {
        return false
      }
    }

    // Non-iOS: just start listening (no permission needed)
    startListening()
    return true
  }, [startListening])

  // Auto-start on Android / desktop (no permission needed)
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Check if DeviceMotionEvent exists at all
    if (!('DeviceMotionEvent' in window)) {
      setIsAvailable(false)
      setMotionMagnitude(0.5)
      return
    }

    // On iOS, we must wait for requestPermission — don't auto-start
    const DME = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<string>
    }
    if (typeof DME.requestPermission === 'function') {
      // iOS: wait for user gesture → requestPermission()
      return
    }

    // Android / desktop: auto-start
    startListening()
  }, [startListening])

  // Cleanup
  useEffect(() => {
    return () => {
      if (listeningRef.current) {
        window.removeEventListener('devicemotion', handleMotion)
        listeningRef.current = false
      }
    }
  }, [handleMotion])

  return { motionMagnitude, isAvailable, requestPermission }
}
